// src/components/ErrorBoundary.jsx
// Catches any JS crash inside the component tree.
// Shows a friendly recovery screen instead of a blank white page.
// Must be a class component — React error boundaries cannot be hooks.
import { Component } from 'react';
import { G, GD } from '../constants';

// S-107 (2026-06-25): Detect a JS chunk-load failure — caused by a stale deploy
// (an old/cached index.html that references chunk hashes which no longer exist).
function isChunkLoadError(error) {
  const msg = (error && (error.message || String(error))) || '';
  return (
    error?.name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    (/MIME type/i.test(msg) && /text\/html/i.test(msg))
  );
}

// S-001A (2026-05-26): Sentry error reporting — production-grade.
// Lazy-required so dev/local builds without DSN don't fail; matches mobile pattern.

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });

    // S-107: Auto-recover from stale-deploy chunk errors. If a JS chunk failed to
    // load (old index.html referencing chunk hashes removed by a newer deploy),
    // hard-reload ONCE to fetch the fresh build instead of showing the crash
    // screen. 10s guard prevents reload loops if a chunk is genuinely missing.
    if (isChunkLoadError(error)) {
      const KEY = 'pc_chunk_reload_ts';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      if (Date.now() - last > 10000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        return; // reloading — skip error reporting & crash UI
      }
    }

    // S-001A: Report to Sentry if available (silent no-op if Sentry not initialized)
    // Same defensive pattern as mobile ErrorBoundary.js — captures real errors,
    // never crashes the ErrorBoundary itself.
    try {
      // eslint-disable-next-line global-require
      const Sentry = require('@sentry/react');
      if (Sentry && Sentry.captureException) {
        Sentry.captureException(error, {
          contexts: { react: { componentStack: info?.componentStack } },
        });
      }
    } catch (_) { /* Sentry not installed or disabled — silent */ }
    console.error('[ErrorBoundary]', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, info: null });
    // Navigate home by reloading — cleanest recovery
    window.location.hash = '';
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: '100vh',
        background: '#080600',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        fontFamily: "'Cinzel', Georgia, serif",
        textAlign: 'center',
      }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>

        <div style={{
          fontWeight: 900,
          fontSize: 20,
          background: `linear-gradient(90deg,${GD},${G})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: 8,
          letterSpacing: 2,
        }}>
          SOMETHING WENT WRONG
        </div>

        <div style={{ color: '#888', fontSize: 13, marginBottom: 28, maxWidth: 320, lineHeight: 1.7 }}>
          An unexpected error occurred. Your account and trades are safe.
          Tap below to restart the app.
        </div>

        {/* Error details — collapsible for power users */}
        <details style={{ marginBottom: 24, maxWidth: 320, textAlign: 'left' }}>
          <summary style={{ color: '#555', fontSize: 11, cursor: 'pointer', marginBottom: 8 }}>
            Technical details
          </summary>
          <div style={{
            background: '#111',
            border: '1px solid #2A2000',
            borderRadius: 8,
            padding: 12,
            color: '#F6465D',
            fontSize: 11,
            fontFamily: 'monospace',
            wordBreak: 'break-word',
            maxHeight: 120,
            overflowY: 'auto',
          }}>
            {this.state.error?.message || 'Unknown error'}
          </div>
        </details>

        <button
          onClick={this.handleReset}
          style={{
            background: `linear-gradient(135deg,${GD},${G})`,
            color: '#0D0A00',
            fontWeight: 800,
            border: 'none',
            borderRadius: 10,
            padding: '14px 32px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 15,
            letterSpacing: 1,
          }}
        >
          🔄 Restart App
        </button>
      </div>
    );
  }
}
